import { useSyncExternalStore } from 'react';
import {
  getMutationFailure,
  getPendingUndo,
  subscribeMutationFailure,
  subscribeUndo,
} from '@/hooks/useMessageMutations';
import { getPendingSend, subscribePendingSend } from '@/features/send-undo/pendingSend';

/**
 * Whether a snackbar is saying something -- a live Undo, a send being held, or
 * the notice either leaves behind.
 *
 * On a phone the prompt and the snackbar share one strip above the bottom bar
 * (both are full-width in the gutters), and the prompt is drawn later, so it
 * sat exactly on top of "Message deleted · Undo": a thumb aimed at Undo landed
 * on Dismiss and the delete went through. A new version can wait five
 * seconds; an Undo cannot. So the prompt is DEFERRED while any snackbar is up
 * and comes back, unchanged, as soon as it closes. Everywhere, not only on
 * handheld: on a desktop the two sit in opposite corners, and one notice at a
 * time in that region is simply calmer.
 */
function subscribeSnackbars(listener: () => void): () => void {
  const offs = [subscribeUndo(listener), subscribeMutationFailure(listener), subscribePendingSend(listener)];
  return () => {
    for (const off of offs) off();
  };
}

function snackbarShowing(): boolean {
  return (
    getPendingUndo() !== undefined ||
    getMutationFailure() !== undefined ||
    getPendingSend() !== undefined
  );
}

export function useSnackbarShowing(): boolean {
  return useSyncExternalStore(subscribeSnackbars, snackbarShowing, snackbarShowing);
}

